import { useState, useEffect, useMemo } from 'react'
import { Link, NavLink, useLocation, useParams, useNavigate } from 'react-router-dom'
import { useUser } from '../../contexts/UserContext'
import { useLeague } from '../../contexts/LeagueContext'
import { auth, announcements } from '../../lib/api'

/**
 * Sidebar - ESPN-style persistent navigation
 *
 * Features:
 * - Dark theme sidebar (slate-900)
 * - Role-based navigation items
 * - League/season context selector with dynamic nav
 * - Quick role switcher
 */
export default function Sidebar({ isOpen, onClose }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, refreshUser, isAuthenticated } = useUser()
  const { currentLeague: contextLeague, allLeagues, allSeasons, currentSeason, selectSeason } = useLeague()
  const [selectedLeague, setSelectedLeague] = useState(null)
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false)
  const [showSeasonDropdown, setShowSeasonDropdown] = useState(false)
  const [subRequestCount, setSubRequestCount] = useState(0)

  // Collapsible section state (persisted in localStorage)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed')
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  })

  const toggleSection = (section) => {
    setCollapsedSections(prev => {
      const updated = { ...prev, [section]: !prev[section] }
      localStorage.setItem('sidebarCollapsed', JSON.stringify(updated))
      return updated
    })
  }

  const activeRole = user?.active_role || user?.role

  // Filter to non-archived leagues from context
  const leagues = useMemo(() => {
    return (allLeagues || []).filter(l => l.archived !== 1)
  }, [allLeagues])

  // Detect if we're on a league page from URL
  const isOnLeaguePage = useMemo(() => {
    return location.pathname.startsWith('/leagues/') ||
           (selectedLeague && location.pathname !== '/dashboard' && location.pathname !== '/')
  }, [location.pathname, selectedLeague])

  // Auto-select first league if none selected when leagues become available
  useEffect(() => {
    if (leagues.length > 0 && !selectedLeague) {
      setSelectedLeague(leagues[0])
    }
  }, [leagues, selectedLeague])

  // Sync selected league with context league when on a league page
  useEffect(() => {
    if (contextLeague && contextLeague.id !== selectedLeague?.id) {
      setSelectedLeague(contextLeague)
    }
  }, [contextLeague])

  // Fetch sub request count for the selected league
  useEffect(() => {
    const fetchSubRequestCount = async () => {
      if (!selectedLeague?.id || !isAuthenticated) {
        setSubRequestCount(0)
        return
      }
      try {
        const subRequests = await announcements.getSubRequests(selectedLeague.id)
        const openCount = (subRequests || []).filter(sr => sr.sub_status === 'open').length
        setSubRequestCount(openCount)
      } catch (error) {
        console.error('Error fetching sub requests:', error)
        setSubRequestCount(0)
      }
    }
    fetchSubRequestCount()
    // Refresh every 60 seconds
    const interval = setInterval(fetchSubRequestCount, 60000)
    return () => clearInterval(interval)
  }, [selectedLeague?.id, isAuthenticated])

  // Determine what sections to show based on ALL user roles (additive)
  const userRoles = user?.roles || [user?.role].filter(Boolean)
  const isGlobalAdmin = userRoles.includes('admin')
  const isOrgManager = userRoles.includes('org_manager') || userRoles.includes('league_manager')
  const isPlayer = userRoles.includes('player')

  // isManager is used for league-specific navigation (join requests, settings)
  const isManager = isGlobalAdmin || isOrgManager

  // Show sections based on actual roles (only if user has that role)
  // Admins only see Admin section unless they're also a player/org manager
  const showPlayerSection = isPlayer
  const showOrgSection = isOrgManager
  const showAdminSection = isGlobalAdmin

  // Get the league base path
  const leagueBasePath = useMemo(() => {
    if (!selectedLeague) return ''
    return selectedLeague.slug ? `/${selectedLeague.slug}` : `/leagues/${selectedLeague.id}`
  }, [selectedLeague])

  // League-specific navigation items (when a league is selected)
  const leagueNavItems = useMemo(() => {
    if (!selectedLeague) return []

    const basePath = leagueBasePath

    const items = [
      { to: basePath, icon: GridIcon, label: 'Overview', exact: true, badge: subRequestCount > 0 ? subRequestCount : null },
      { to: `${basePath}/schedule`, icon: CalendarIcon, label: 'Schedule' },
      { to: `${basePath}/standings`, icon: TrophyIcon, label: 'Standings' },
      { to: `${basePath}/teams`, icon: UsersIcon, label: 'Teams' },
      { to: `${basePath}/stats`, icon: StatsIcon, label: 'Stats' },
      { to: `${basePath}/inbox`, icon: InboxIcon, label: 'Messages' },
    ]

    // Add league-specific manager tools
    if (isManager) {
      items.push(
        { to: `${basePath}/join-requests`, icon: BellIcon, label: 'Join Requests' },
        { to: `${basePath}/admin`, icon: WrenchIcon, label: 'Settings' }
      )
    }

    return items
  }, [selectedLeague, leagueBasePath, isManager, subRequestCount])

  // General navigation items (org-level)
  const playerNavItems = [
    { to: '/discover-teams', icon: JerseyIcon, label: 'Find Teams' },
  ]

  const managerNavItems = [
    { to: '/dashboard', icon: OrgIcon, label: 'My Organization' },
    { to: '/billing', icon: BillingIcon, label: 'Billing' },
  ]

  // Admin-specific navigation (global admins managing all orgs/users)
  const adminNavItems = [
    { to: '/admin/organizations', icon: OrgIcon, label: 'Organizations' },
    { to: '/admin/users', icon: UsersIcon, label: 'Users' },
    { to: '/admin/billing', icon: BillingIcon, label: 'Billing' },
    { to: '/admin/notifications', icon: BellIcon, label: 'Notifications' },
    { to: '/admin/system-alerts', icon: AlertIcon, label: 'System Alerts' },
  ]

  // Handle league selection and navigation
  const handleLeagueSelect = (league) => {
    setSelectedLeague(league)
    setShowLeagueDropdown(false)
    // Navigate to the league's overview page
    const path = league.slug ? `/${league.slug}` : `/leagues/${league.id}`
    navigate(path)
    onClose?.()
  }

  // Handle role switch
  const handleRoleSwitch = async (role) => {
    if (role !== activeRole) {
      try {
        await auth.switchRole(role)
        refreshUser()
        window.location.reload()
      } catch (err) {
        console.error('Failed to switch role:', err)
      }
    }
  }

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await auth.signOut()
      sessionStorage.clear()
      localStorage.clear()

      // Redirect to main domain with logout flag
      const hostname = window.location.hostname
      const protocol = window.location.protocol
      const port = window.location.port

      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        window.location.href = `${protocol}//localhost${port ? ':' + port : ''}/?logout=true`
      } else {
        const parts = hostname.split('.')
        const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname
        window.location.href = `${protocol}//${baseDomain}/?logout=true`
      }
    } catch (err) {
      console.error('Failed to sign out:', err)
    }
  }

  // Get effective roles (filter redundant ones)
  const effectiveRoles = user?.roles?.filter(role => {
    if (role === 'league_manager' && user.roles.includes('org_manager')) return false
    return true
  }) || []

  // Render nav item helper
  const renderNavItem = (item) => {
    const isActive = item.exact
      ? location.pathname === item.to
      : location.pathname === item.to || location.pathname.startsWith(item.to + '/')

    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={onClose}
        className={`
          flex items-center gap-3 px-3 py-2.5 rounded-lg
          transition-all duration-200 font-medium text-sm
          ${isActive
            ? 'bg-ice-600 text-white'
            : 'text-slate-400 hover:text-white hover:bg-slate-800'
          }
        `}
      >
        <item.icon className="w-5 h-5" />
        <span className="flex-1">{item.label}</span>
        {item.badge && (
          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-orange-500 text-white rounded-full">
            {item.badge}
          </span>
        )}
      </NavLink>
    )
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-slate-900 text-slate-100
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src="/shield-logo.png"
              alt="IceOps"
              className="h-9 w-auto"
            />
            <span className="text-xl font-bold text-white">IceOps</span>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-slate-400 hover:text-white rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* League Selector */}
        {leagues.length > 0 && (
          <div className="px-3 py-4 border-b border-slate-800 flex-shrink-0">
            <button
              onClick={() => setShowLeagueDropdown(!showLeagueDropdown)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
            >
              <div className="text-left flex-1 min-w-0">
                <div className="text-xs text-slate-400 uppercase tracking-wide">League</div>
                <div className="text-sm font-medium text-white truncate">
                  {selectedLeague?.name || 'Select League'}
                </div>
              </div>
              <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${showLeagueDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showLeagueDropdown && (
              <div className="mt-2 py-1 bg-slate-800 rounded-lg max-h-64 overflow-y-auto">
                {leagues.map((league) => (
                  <button
                    key={league.id}
                    onClick={() => handleLeagueSelect(league)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 transition-colors ${
                      selectedLeague?.id === league.id ? 'bg-slate-700 text-ice-400' : 'text-slate-300'
                    }`}
                  >
                    {league.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {/* League Navigation - when league is selected */}
          {selectedLeague && leagueNavItems.length > 0 && (
            <div className="space-y-1">
              {leagueNavItems.map(renderNavItem)}
            </div>
          )}

          {/* Player Section */}
          {showPlayerSection && (
            <>
              <div className="my-4">
                <div className="border-t border-slate-800" />
                <button
                  onClick={() => toggleSection('player')}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800 rounded transition-colors"
                >
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Player
                  </span>
                  <ChevronIcon className={`w-4 h-4 text-slate-500 transition-transform ${collapsedSections.player ? '-rotate-90' : ''}`} />
                </button>
              </div>
              {!collapsedSections.player && (
                <div className="space-y-1">
                  {playerNavItems.map(renderNavItem)}
                </div>
              )}
            </>
          )}

          {/* Organization Section */}
          {showOrgSection && (
            <>
              <div className="my-4">
                <div className="border-t border-slate-800" />
                <button
                  onClick={() => toggleSection('organization')}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800 rounded transition-colors"
                >
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Organization
                  </span>
                  <ChevronIcon className={`w-4 h-4 text-slate-500 transition-transform ${collapsedSections.organization ? '-rotate-90' : ''}`} />
                </button>
              </div>
              {!collapsedSections.organization && (
                <div className="space-y-1">
                  {managerNavItems.map(renderNavItem)}
                </div>
              )}
            </>
          )}

          {/* Admin Section */}
          {showAdminSection && (
            <>
              <div className="my-4">
                <div className="border-t border-slate-800" />
                <button
                  onClick={() => toggleSection('admin')}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800 rounded transition-colors"
                >
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Admin
                  </span>
                  <ChevronIcon className={`w-4 h-4 text-slate-500 transition-transform ${collapsedSections.admin ? '-rotate-90' : ''}`} />
                </button>
              </div>
              {!collapsedSections.admin && (
                <div className="space-y-1">
                  {adminNavItems.map(renderNavItem)}
                </div>
              )}
            </>
          )}
        </nav>

        {/* Bottom Section - Role Switcher & User */}
        <div className="border-t border-slate-800 p-3 flex-shrink-0">
          {/* Role Switcher */}
          {effectiveRoles.length > 1 && (
            <div className="mb-3 p-3 bg-slate-800 rounded-lg">
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">View As</div>
              <div className="flex gap-1">
                {effectiveRoles.map((role) => (
                  <button
                    key={role}
                    onClick={() => handleRoleSwitch(role)}
                    className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors capitalize ${
                      role === activeRole
                        ? 'bg-ice-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {role.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* User Info */}
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-ice-600 flex items-center justify-center text-white font-semibold text-sm">
              {(user?.name || user?.email || 'U')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {user?.name || user?.email}
              </div>
              <div className="text-xs text-slate-400 capitalize">
                {activeRole?.replace('_', ' ')}
              </div>
            </div>
          </div>

          {/* Account & Logout */}
          <div className="flex gap-2 px-3 pt-2">
            <NavLink
              to="/settings"
              onClick={onClose}
              className={({ isActive }) => `
                flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-ice-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                }
              `}
            >
              <SettingsIcon className="w-4 h-4" />
              Account
            </NavLink>
            <button
              onClick={handleSignOut}
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-red-600 hover:text-white transition-colors"
            >
              <LogoutIcon className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

// Icon components
function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  )
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
}

function TrophyIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  )
}

function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  )
}

function StatsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
}

function MegaphoneIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  )
}

function SettingsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function InboxIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  )
}

function GridIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  )
}

function WrenchIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
    </svg>
  )
}

function BellIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  )
}

function JerseyIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2m10 2V2M5 8h14M5 8a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V10a2 2 0 00-2-2M5 8l2-4h10l2 4M12 12v6" />
    </svg>
  )
}

function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function BillingIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  )
}

function OrgIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  )
}

function LogoutIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  )
}

function ShieldIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  )
}

function AlertIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  )
}

function ChevronIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

